import { CommandLoading } from "@hortis/ui/command";
import { toast } from "@hortis/ui/sonner";
import { useQueryClient } from "@tanstack/react-query";
import { useParams, useRouteContext } from "@tanstack/react-router";
import {
  type MaterialsListFieldsFragment,
  namedOperations,
  useCollectionSiteTagsQuery,
} from "generated/graphql";
import { useCallback } from "react";
import { useActiveRecordTags } from "src/features/collection/components/plant-materials/utils";
import {
  addTagToMaterialsDocument,
  removeTagFromMaterialsDocument,
} from "src/features/records/material/graphql";
import { useGqlMutation } from "src/utils/hooks/graphql";
import { CommandMenuTagsPage } from "../tags";
import { ErrorAlert } from "./taxon-tags";

interface CommandMenuMaterialTagsPageProps {
  open: boolean;
  selectedRows: Array<MaterialsListFieldsFragment>;
  menuPage: string;
  search: string;
  selectedRecordsIDs: Array<string>;
  clearSearch: () => void;
}

const CommandMenuMaterialTagsPage = ({
  open,
  selectedRows,
  menuPage,
  search,
  clearSearch,
  selectedRecordsIDs,
}: CommandMenuMaterialTagsPageProps) => {
  const { subdomain } = useRouteContext({ from: "/_layout/site/$siteSlug" });
  const { siteSlug } = useParams({ from: "/_layout/site/$siteSlug" });
  const queryClient = useQueryClient();

  const addTagToMaterials = useGqlMutation({
    mutation: addTagToMaterialsDocument,
    onError: () => {
      toast.error("Failed to add tag to plant materials");
    },
  });
  const removeTagFromMaterials = useGqlMutation({
    mutation: removeTagFromMaterialsDocument,
    onError: () => {
      toast.error("Failed to remove tag from plant materials");
    },
  });
  const [{ data: tagsData, fetching, error }, refetchTags] =
    useCollectionSiteTagsQuery({
      variables: {
        organisationSubdomain: subdomain,
        collectionSiteSlug: siteSlug,
        orgFilters: {
          hasCollectionSiteRelation: {
            eq: false,
          },
        },
      },
    });

  const [activeRecordTags, setActiveRecordTags] = useActiveRecordTags(
    selectedRows,
    open,
  );
  const orgTags = tagsData?.org?.tags?.nodes ?? [];
  const siteTags = tagsData?.org?.site?.tags?.nodes ?? [];

  const allTags = [...orgTags, ...siteTags].sort((a, b) =>
    a.name.localeCompare(b.name),
  );

  const hasTags = allTags.length > 0;
  const isTagsPage = menuPage === "tags";
  const shouldShowHeading = !isTagsPage || hasTags;

  const removeTagFromSelectedRecords = useCallback(
    async (tagId: string) => {
      const res = await removeTagFromMaterials.mutateAsync({
        tagId,
        ids: selectedRecordsIDs,
      });
      const removeTagSuccessful =
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- Tests flake seeming to return nullable removeTagPlantMaterials
        res.removeTagPlantMaterials?.success ?? false;

      if (removeTagSuccessful) {
        toast.success(
          `Tag removed from ${selectedRecordsIDs.length} plant materials`,
        );
        void queryClient.invalidateQueries({
          queryKey: [namedOperations.Query.PlantMaterials],
        });
      } else {
        toast.error("Failed to remove tag from plant materials");
      }
      refetchTags();
      return removeTagSuccessful;
    },
    [refetchTags, removeTagFromMaterials, selectedRecordsIDs, queryClient],
  );

  const applyTagToSelectedRecords = useCallback(
    async (tagId: string) => {
      const res = await addTagToMaterials.mutateAsync({
        tagId,
        ids: selectedRecordsIDs,
      });

      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- Tests flake seeming to return nullable removeTagPlantMaterials
      const addTagSuccessful = res.addTagPlantMaterials?.success ?? false;
      if (addTagSuccessful) {
        toast.success(
          `Tag added to ${selectedRecordsIDs.length} plant materials`,
        );
        void queryClient.invalidateQueries({
          queryKey: [namedOperations.Query.PlantMaterials],
        });
        refetchTags();
      } else {
        toast.error("Failed to add tag to plant materials");
      }
      return addTagSuccessful;
    },

    [addTagToMaterials, refetchTags, selectedRecordsIDs, queryClient],
  );

  if (tagsData == null && fetching) {
    return <CommandLoading />;
  }

  if (error != null) {
    return <ErrorAlert message="Something went wrong." />;
  }

  return (
    <CommandMenuTagsPage
      allTags={allTags}
      shouldShowHeading={shouldShowHeading}
      selectedRecordsWithTags={activeRecordTags}
      setSelectedRecordsWithTags={setActiveRecordTags}
      hasTags={hasTags}
      search={search}
      clearSearch={clearSearch}
      applyTagToSelectedRecords={applyTagToSelectedRecords}
      removeTagFromSelectedRecords={removeTagFromSelectedRecords}
    />
  );
};

export { CommandMenuMaterialTagsPage };
